import type { Route } from 'vue-router';
import AuthService from '@/services/authService.ts';
import { setDeepBoxBackRoute } from '@/composables/useUtils';
import { DocumentStatus } from '@/types/enums/DocumentStatus';
import { getCurrentStatusPage } from '@/services/documentService';
import { globalRouterReference } from '@/router/globalRouterReference';
import { useErrorStore } from '@/stores/error.ts';
import { AxiosError } from 'axios';
import { useCreateStore } from '@/stores/create.ts';
import { useApi } from '@/composables/useApi.ts';
import { HttpClientError, isHttpClientError } from '@deepcloud/api-http-client';

interface Filters {
  limit: number;
  filterDeepboxDocumentId?: string;
  statuses?: string;
  order: string;
}

function getRouteKey(routePath: string) {
  return routePath.substring(routePath.lastIndexOf('/') + 1);
}

function setBackRoute(route: Route) {
  const backRoute = route.query?.backUrl?.toString().substring(1) || '';
  setDeepBoxBackRoute(AuthService.user?.sub, backRoute);
}

async function handleDocumentCreation(route: Route) {
  const createStore = useCreateStore();
  try {
    await createStore.createDocumentFromDeepBox({
      deepboxDocumentId: route.params.id as string,
      scanPredefinedSignees: true,
      addInitiatorAsSignee: true,
      signeeAttachmentsAllowed: false,
      applyCompanySettingsDefaults: true
    });
    if (createStore.document) {
      await globalRouterReference.router!.replace({
        name: 'document-create',
        params: { document: createStore.document?.documentId },
        query: { ...route.query }
      });
    }
    createStore.document = undefined;
  } catch (error: unknown) {
    if (isHttpClientError(error) && error.response?.status === 409) {
      console.info('Document already exists, redirecting to the correct page');
      await fetchDocuments(route, {
        limit: 1,
        filterDeepboxDocumentId: route.params.id as string,
        order: 'creationTime desc'
      });
    } else {
      const errorStore = useErrorStore();
      errorStore.error = error as HttpClientError;
      globalRouterReference.router!.push({ name: 'error-page' });
      console.error('Error creating document', error);
    }
  }
}

export async function fetchDocuments(route: Route, filters: Filters) {
  const { deepSignAPI } = useApi();
  try {
    const documentsResponse = await deepSignAPI.value.documents.get(filters);
    const documents = documentsResponse.data.documents;
    if (documents && documents.length > 0) {
      const lastDocument = documents[0];
      if (lastDocument) {
        const destination = getCurrentStatusPage(
          AuthService.user?.email,
          lastDocument
        );
        if (getRouteKey(destination) !== getRouteKey(route.path)) {
          await globalRouterReference.router!.push({
            path: destination,
            query: { ...route.query }
          });
        }
      }
    } else {
      await globalRouterReference.router!.push({ name: '404' });
    }
  } catch (error) {
    const errorStore = useErrorStore();
    errorStore.error = error as AxiosError;
    globalRouterReference.router!.push({ name: 'error-page' });
    console.error('Error creating document', error);
  }
}

const deepBoxDocumentGuardian = () => {
  return async (to: Route, _from: Route) => {
    setBackRoute(to);
    switch (to.name) {
      // If you come from /deepbox/:id
      case 'deepbox-id':
        await handleDocumentCreation(to);
        break;
      // If you come from /deepbox/:id/pending
      case 'deepbox-id-pending':
        await fetchDocuments(to, {
          limit: 1,
          filterDeepboxDocumentId: to.params.id as string,
          statuses: [DocumentStatus.IN_PROGRESS, DocumentStatus.DRAFT].join(),
          order: 'creationTime desc'
        });
        break;
      // If you come from /deepbox/:id/signed
      default:
        await fetchDocuments(to, {
          limit: 1,
          filterDeepboxDocumentId: to.params.id as string,
          statuses: [DocumentStatus.SIGNED].join(),
          order: 'creationTime desc'
        });
    }
  };
};

export default deepBoxDocumentGuardian;
